import React, { useRef, useEffect } from "react";
import ModernFieldRenderer from "./ModernFieldRenderer";
import Tooltip from "./Tooltip";
import dateUtils from "../../utils/dateUtils";

const ModernQuestionnaireFormSection = ({
  currentSection,
  formData,
  handleChange,
  handleMultiSelect,
  basicInfoLockedFromTrip = false,
}) => {
  const shouldShowInfoField = (field) => {
    if (!field.dependsOn) return true;
    const { field: dependsOnField, value: depVal } = field.dependsOn;

    // Fantasmic Dining: use exact same "has Fantasmic" check as validation so the question always shows when validation would require it
    if (field.id === "fantasmicDiningPackageInterest") {
      const mustSee =
        formData?.showFireworkStrategy?.mustSeeShows ??
        (currentSection?.id === "showFireworkStrategy" ? formData?.[currentSection.id]?.mustSeeShows : undefined);
      const raw = Array.isArray(mustSee) ? mustSee : [];
      return raw.some((item) => {
        const name = typeof item === "string" ? item : item?.name;
        if (name == null) return false;
        const s = String(name).trim().replace(/\uFF01/g, "!");
        if (s === "Fantasmic!" || s === "Fantasmic") return true;
        // Lenient match so we show the question even if label differs slightly
        return s.toLowerCase().replace(/!+$/, "").startsWith("fantasmic");
      });
    }

    // Map dependent field IDs to their section for nested form data
    const fieldToSection = {
      hasParkHopper: "parkTicketAccess",
      includeRestDays: "parkVisitPlans",
      stayingOnProperty: "accommodation",
      resort: "accommodation",
      hasDisability: "specialConsiderations",
      celebration: "specialConsiderations",
      celebrationDetails: "specialConsiderations",
      celebrationDate: "specialConsiderations",
      celebrationOtherDetails: "specialConsiderations",
      additionalInfo: "specialConsiderations",
      enchantedExperiences: "specialtyExperiences",
      interestedInCharacterMeetGreets: "characterMeetGreets",
      mustMeetCharacters: "characterMeetGreets",
      mustMeetCharactersOther: "characterMeetGreets",
      showStrategy: "showFireworkStrategy",
      mustSeeShows: "showFireworkStrategy",
      fantasmicDiningPackageInterest: "showFireworkStrategy",
      fantasmicDiningPackageRestaurants: "showFireworkStrategy",
    };
    
    const section = fieldToSection[dependsOnField];
    let fieldValue = section && formData[section]
      ? formData[section][dependsOnField]
      : formData[dependsOnField];
    
    if (depVal === "[any]") {
      const hasValue = fieldValue != null && fieldValue !== "" &&
        (typeof fieldValue !== "string" || fieldValue.trim() !== "");
      return !!hasValue;
    }
    if (typeof depVal === "string" && depVal.startsWith("[includes:") && depVal.endsWith("]")) {
      const needle = depVal.slice(9, -1).trim();
      // Resolve value: prefer section lookup, then explicit showFireworkStrategy.mustSeeShows, then mustMeetCharacters from characterMeetGreets, then top-level
      let resolvedValue = fieldValue;
      if (resolvedValue === undefined && dependsOnField === "mustSeeShows" && formData?.showFireworkStrategy) {
        resolvedValue = formData.showFireworkStrategy.mustSeeShows;
      }
      // Meet & Greet "Other": mustMeetCharacters is nested under characterMeetGreets; ensure we read from the correct path
      if (dependsOnField === "mustMeetCharacters" && formData?.characterMeetGreets) {
        resolvedValue = formData.characterMeetGreets.mustMeetCharacters;
      }
      if (resolvedValue === undefined && section && formData[section]) {
        resolvedValue = formData[section][dependsOnField];
      }
      if (resolvedValue === undefined) resolvedValue = formData[dependsOnField];
      const raw = Array.isArray(resolvedValue) ? resolvedValue : (resolvedValue != null ? [resolvedValue] : []);
      const matchesNeedle = (nameStr) => {
        if (nameStr == null) return false;
        const s = String(nameStr).trim();
        if (s === needle) return true;
        // Match "Fantasmic!" when needle is "Fantasmic!" or "Fantasmic"
        const needleBase = needle.replace(/!+$/, "").replace(/\uFF01/g, "!");
        return needleBase.length > 0 && (s === needleBase || s === needleBase + "!");
      };
      return raw.some((item) => {
        const name = typeof item === "string" ? item : item?.name;
        return matchesNeedle(name);
      });
    }
    return fieldValue === depVal;
  };

  const isParkSection = currentSection.id === "parkVisitPlans";
  const fantasmicBlockRef = useRef(null);

  // Scroll Fantasmic dining block into view when it appears (user selected Fantasmic!)
  useEffect(() => {
    if (currentSection?.id !== "showFireworkStrategy" || !fantasmicBlockRef.current) return;
    const mustSee = formData?.showFireworkStrategy?.mustSeeShows;
    const raw = Array.isArray(mustSee) ? mustSee : [];
    const hasFantasmic = raw.some((item) => {
      const name = typeof item === "string" ? item : item?.name;
      return name != null && (String(name).trim() === "Fantasmic!" || String(name).trim() === "Fantasmic");
    });
    if (hasFantasmic) {
      fantasmicBlockRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [currentSection?.id, formData?.showFireworkStrategy?.mustSeeShows]);

  const totalParkDays = [
    "mkDays",
    "hsDays",
    "epcotDays",
    "akDays",
    "tlDays",
    "bbDays",
  ].reduce(
    (total, field) => total + (parseInt(formData.parkVisitPlans?.[field] || "0", 10) || 0),
    0
  );

  const totalTripDays = (() => {
    const start = new Date(formData.basicInformation?.startDate);
    const end = new Date(formData.basicInformation?.endDate);
    if (isNaN(start) || isNaN(end)) return 0;
    const diff = (end - start) / (1000 * 60 * 60 * 24) + 1;
    return Math.round(diff);
  })();

  const eligibleParkDays = Math.max(totalTripDays - 2, 0);
  const remainingParkDays = eligibleParkDays - totalParkDays;
  const hasFlexibleAccess =
    formData.parkTicketAccess?.ticketType === "Annual Pass" || formData.parkTicketAccess?.hasParkHopper === "Yes";

  const formMeta = {
    totalTripDays,
    eligibleParkDays,
    remainingParkDays,
    totalParkDays,
    hasFlexibleAccess,
    basicInfoLockedFromTrip,
  };

  const renderParkDaysWarning = () => {
    if (!isParkSection) return null;

    let statusColor = "text-blue-400";
    let progressColor = "bg-blue-500";
    
    if (totalParkDays === 0) {
      statusColor = "text-red-400";
      progressColor = "bg-red-500";
    } else if (remainingParkDays < 0) {
      // Over the limit
      if (hasFlexibleAccess) {
        statusColor = "text-yellow-400";
        progressColor = "bg-yellow-500";
      } else {
        statusColor = "text-red-400";
        progressColor = "bg-red-500";
      }
    } else if (remainingParkDays <= 1) {
      // Close to being over (1 day or less remaining)
      statusColor = "text-yellow-400";
      progressColor = "bg-yellow-500";
    }

    return (
      <div className="p-4 mt-6 bg-gray-700 rounded-lg border border-gray-600 shadow-sm">
        <div className="space-y-3">
          {/* Header */}
          <div className="flex justify-between items-center">
            <h3 className="text-base font-semibold text-white">Trip Overview</h3>
            <span className="px-2 py-1 text-xs font-medium text-gray-200 bg-gray-600 rounded-full">
              Total Trip Days: {totalTripDays} (including arrival and departure days)
            </span>
          </div>
          
          {/* Park Days Summary */}
          <div className="flex justify-between items-center p-3 bg-gray-600 rounded-lg border border-gray-500">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-200">Park Days Selected</span>
              {hasFlexibleAccess && (
                <span className="text-xs text-blue-400">✨ Park Hopper</span>
              )}
            </div>
            <span className={`text-lg font-bold ${
              remainingParkDays >= 0 ? 'text-green-400' : 'text-orange-400'
            }`}>
              {totalParkDays}
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between items-center text-xs text-gray-300">
              <span>Progress</span>
              <span>
                {remainingParkDays >= 0
                  ? `${remainingParkDays} day${remainingParkDays !== 1 ? 's' : ''} remaining`
                  : `Over by ${Math.abs(remainingParkDays)} day${Math.abs(remainingParkDays) !== 1 ? 's' : ''}`}
              </span>
            </div>
            <div className="overflow-hidden w-full h-2 bg-gray-600 rounded-full">
              <div
                className={`h-2 rounded-full transition-all duration-500 ease-out ${
                  remainingParkDays >= 0 ? 'bg-green-500' : 'bg-orange-500'
                }`}
                style={{
                  width: `${
                    eligibleParkDays > 0
                      ? Math.min((totalParkDays / eligibleParkDays) * 100, 100)
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 mb-8">
      {(() => {
        let parkWarningInserted = false;

        return currentSection.fields.map((field, index) => {
          if (field.type === "dynamicAgeDropdown") return null;
          if (field.dependsOn && !shouldShowInfoField(field)) return null;

          // Conditionally hide numAdultMales and numAdultFemales if adults is 0
          if (
            (field.id === "numAdultMales" || field.id === "numAdultFemales") &&
            parseInt(formData.groupInfo?.adults || "0", 10) === 0
          ) {
            return null;
          }

          // Conditionally hide numBoys and numGirls if kids is 0
          if (
            (field.id === "numBoys" || field.id === "numGirls") &&
            parseInt(formData.groupInfo?.kids || "0", 10) === 0
          ) {
            return null;
          }

          // Conditionally hide park day fields if their corresponding park is not selected
          const selectedParks = formData.parkVisitPlans?.parks || [];
          const parkDayFieldMap = {
            mkDays: "Magic Kingdom",
            hsDays: "Hollywood Studios", 
            epcotDays: "EPCOT",
            akDays: "Animal Kingdom",
            tlDays: "Typhoon Lagoon",
            bbDays: "Blizzard Beach"
          };
          
          if (parkDayFieldMap[field.id] && !selectedParks.includes(parkDayFieldMap[field.id])) {
            return null;
          }

          const shouldInsertParkWarning =
            isParkSection &&
            !parkWarningInserted &&
            field.id === "parks";

          if (shouldInsertParkWarning) {
            parkWarningInserted = true;
          }

          const wrapFantasmicBlock = field.id === "fantasmicDiningPackageInterest";

          // Trip Dates tooltip and short-trip warning (Basic Information section only)
          const isBasicInfoDateSection =
            currentSection.id === "basicInformation" && field.id === "startDate";
          const tripLengthDays = dateUtils.getTripLengthDays(
            formData.basicInformation?.startDate,
            formData.basicInformation?.endDate
          );
          const showShortTripWarning =
            tripLengthDays !== null && tripLengthDays >= 1 && tripLengthDays <= 2;

          return (
            <React.Fragment key={field.id}>
              {isBasicInfoDateSection && (
                <div className="mb-4 space-y-2">
                  <div className="inline-flex items-center gap-1.5">
                    <span
                      className="text-sm font-semibold text-gray-800 leading-none"
                      style={{ fontFamily: 'Tahoma, "Segoe UI", Arial, sans-serif' }}
                    >
                      Trip Dates
                    </span>
                    <Tooltip
                      message="If your trip is 1–2 days, ParkPro builds only park days (no Arrival/Departure days). If your trip is 3+ days, ParkPro includes an Arrival day and a Departure day (if applicable)."
                    >
                      <span
                        className="inline-flex items-center justify-center w-5 h-5 text-gray-600 cursor-pointer text-xs font-medium border border-gray-500 rounded-full leading-none shrink-0"
                        aria-label="Trip length rules"
                      >
                        ?
                      </span>
                    </Tooltip>
                  </div>
                  {showShortTripWarning && (
                    <div
                      className="flex items-start gap-2 p-3 text-sm text-amber-800 bg-amber-50 rounded-lg border border-amber-200"
                      role="status"
                    >
                      <svg
                        className="flex-shrink-0 w-5 h-5 text-amber-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        aria-hidden
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>
                        Short trip detected (1–2 days). ParkPro will build park days only — Arrival/Departure days are skipped.
                      </span>
                    </div>
                  )}
                </div>
              )}
              {wrapFantasmicBlock ? (
                <div ref={fantasmicBlockRef} className="mt-6 pt-4 border-t border-gray-200" data-fantasmic-dining-block>
                  <ModernFieldRenderer
                    field={field}
                    formData={formData}
                    handleChange={handleChange}
                    handleMultiSelect={handleMultiSelect}
                    formMeta={formMeta}
                  />
                </div>
              ) : (
                <ModernFieldRenderer
                  field={field}
                  formData={formData}
                  handleChange={handleChange}
                  handleMultiSelect={handleMultiSelect}
                  formMeta={formMeta}
                />
              )}

              {shouldInsertParkWarning && renderParkDaysWarning()}

              {/* Ages of Boys */}
              {field.id === "numBoys" &&
                parseInt(formData.groupInfo?.numBoys || "0", 10) > 0 && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Please select the age of each boy
                    </label>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {Array.from({ length: parseInt(formData.groupInfo?.numBoys, 10) }).map(
                        (_, i) => (
                          <select
                            key={`boyAge-${i}`}
                            className="px-3 py-2 w-full text-sm font-medium text-white bg-gray-700 rounded-lg border-2 border-gray-600 transition-all duration-200 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500"
                            value={formData.groupInfo?.agesOfBoys?.[i] || ""}
                            onChange={(e) => {
                              const updated = [...(formData.groupInfo?.agesOfBoys || [])];
                              updated[i] = e.target.value;
                              handleChange("agesOfBoys", updated);
                            }}
                          >
                            <option value="">Select age</option>
                            {Array.from({ length: 18 }, (_, j) => `${j + 1}`).map(
                              (age) => (
                                <option key={age} value={age}>
                                  Age of Boy #{i + 1}: {age}
                                </option>
                              )
                            )}
                          </select>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* Ages of Girls */}
              {field.id === "numGirls" &&
                parseInt(formData.groupInfo?.numGirls || "0", 10) > 0 && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Please select the age of each girl
                    </label>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {Array.from({ length: parseInt(formData.groupInfo?.numGirls, 10) }).map(
                        (_, i) => (
                          <select
                            key={`girlAge-${i}`}
                            className="px-3 py-2 w-full text-sm font-medium text-white bg-gray-700 rounded-lg border-2 border-gray-600 transition-all duration-200 appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-500"
                            value={formData.groupInfo?.agesOfGirls?.[i] || ""}
                            onChange={(e) => {
                              const updated = [...(formData.groupInfo?.agesOfGirls || [])];
                              updated[i] = e.target.value;
                              handleChange("agesOfGirls", updated);
                            }}
                          >
                            <option value="">Select age</option>
                            {Array.from({ length: 18 }, (_, j) => `${j + 1}`).map(
                              (age) => (
                                <option key={age} value={age}>
                                  Age of Girl #{i + 1}: {age}
                                </option>
                              )
                            )}
                          </select>
                        )
                      )}
                    </div>
                  </div>
                )}
            </React.Fragment>
          );
        });
      })()}
    </div>
  );
};

export default ModernQuestionnaireFormSection;
